import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
    type ReactNode,
} from 'react';
import { repairersApi } from '@/api/repairers';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';
import type { Repairer } from '@/types/types';

export interface RepairersContextValue {
    state: { repairers: Repairer[]; loading: boolean };
    actions: {
        refreshRepairers: () => Promise<void>;
    };
}

const RepairersContext = createContext<RepairersContextValue | null>(null);

export const RepairersProvider = ({ children }: { children: ReactNode }) => {
    const [repairers, setRepairers] = useState<Repairer[]>([]);
    const [loading, setLoading] = useState(false);
    const auth = useAuth();
    const loggedIn = auth.state.loggedIn;

    useEffect(() => {
        if (!loggedIn) {
            setRepairers([]);
            setLoading(false);
        }
    }, [loggedIn]);

    const refreshRepairers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await repairersApi.list();
            setRepairers(res.data?.payload ?? []);
        } catch {
            setRepairers([]);
        } finally {
            setLoading(false);
        }
    }, []);

    return (
        <RepairersContext.Provider
            value={{
                state: { repairers, loading },
                actions: { refreshRepairers },
            }}
        >
            {children}
        </RepairersContext.Provider>
    );
};

export const useRepairers = (): RepairersContextValue => {
    const ctx = useContext(RepairersContext);
    if (!ctx) throw new Error('useRepairers must be used inside RepairersProvider');
    return ctx;
};
